import Icon from "../icon/icon.js";
/**
 * `<Expansion>` — an accordion of collapsible panels (WAI-ARIA accordion pattern).
 *
 * Each panel is a `<button>` header (title + a `+`/`−` marker) that toggles a
 * `role=region` body revealed below it. The open header takes the `--field` tint;
 * the body animates open via a `grid-template-rows` transition. **Panels are
 * independent by default** (`multi` — any number open); pass `multi={{ false }}`
 * for single-open (opening one closes the rest).
 *
 * Structural metadata is an items-prop (`panels`); each panel's **body is arbitrary
 * content** — a `Node`, a string, or a factory `() => Node` (same shape as Dialog
 * content) — appended into its region on mount. Open state is controlled via
 * `value` (open panel ids) + `onChange`, or uncontrolled from `defaultOpen`.
 *
 * a11y: header `<button aria-expanded aria-controls>` inside a `role=heading`
 * (level via `headingLevel`, default 3); region `role=region aria-labelledby`,
 * `inert` + `aria-hidden` while collapsed. Up/Down/Home/End move focus between
 * enabled headers; Enter/Space toggle (native button).
 *
 *   import Expansion from '@weave-framework/ui/expansion';
 *   <Expansion panels={{ panels }} value={{ open() }} onChange={{ setOpen }} />
 */

import { signal, onMount, type Signal } from '@weave-framework/runtime';

/** A panel body: a DOM node, a plain string, or a factory returning a node. */
export type ExpansionContent = Node | string | (() => Node);

export interface ExpansionPanel {
  /** Stable id — what `value`/`onChange` speak in, and the open-set key. */
  id: string;
  /** Header title text. */
  header: string;
  /** Body content, revealed when the panel is open. */
  body: ExpansionContent;
  /** Disable just this panel (not toggleable, skipped in keyboard nav). */
  disabled?: boolean;
}

export interface ExpansionProps {
  /** The panels, top to bottom. */
  panels: ExpansionPanel[];
  /** Independent panels (any number open). Default true; false = single-open. */
  multi?: boolean;
  /** Controlled open set: the ids of the open panels. */
  value?: string[];
  /** Called with the next open set on toggle. */
  onChange?: (openIds: string[]) => void;
  /** Uncontrolled initial open set (ignored when `value` is provided). */
  defaultOpen?: string[];
  /** Disable the whole accordion. */
  disabled?: boolean;
  /** aria-level for each header's heading wrapper. Default 3. */
  headingLevel?: number;
  /** Extra classes, forwarded onto the container. */
  class?: string;
}

                               
                                                  
                                                
                                          
                                                                                           
                                                                                    
                                                                                  
                                                                                 
                                        
                                                                    
                                                                                                           
               
            
                                                                                 
                                                                            
                                                                        
                                                                                           
            
            
       
           

export interface ExpansionContext {
  host: Signal<Element | null>;
  panels: () => ExpansionPanel[];
  rootClass: () => string;
  headingLevel: () => number;
  headerId: (panel: ExpansionPanel) => string;
  regionId: (panel: ExpansionPanel) => string;
  expandedAttr: (panel: ExpansionPanel) => string;
  /** lucide icon name for the open/closed marker — `minus` when open, `plus` when closed. */
  markerIcon: (panel: ExpansionPanel) => string;
  openAttr: (panel: ExpansionPanel) => string | undefined;
  hiddenAttr: (panel: ExpansionPanel) => string | undefined;
  disabledAttr: (panel: ExpansionPanel) => string | undefined;
  isClosed: (panel: ExpansionPanel) => boolean;
  toggle: (panel: ExpansionPanel) => void;
  onHeaderKeydown: (event: KeyboardEvent) => void;
}

let _uid: number = 0;

function toNode(content: ExpansionContent): Node {
  if (typeof content === 'function') return content();
  if (typeof content === 'string') return document.createTextNode(content);
  return content;
}

export function setup(props: ExpansionProps): ExpansionContext {
  const host: Signal<Element | null> = signal<Element | null>(null);
  const uid: number = (_uid += 1);
  const uncontrolled: Signal<string[]> = signal<string[]>(props.defaultOpen ?? []);

  const panels = (): ExpansionPanel[] => props.panels ?? [];
  const multi = (): boolean => props.multi !== false;
  const openIds = (): string[] => (props.value !== undefined ? (props.value ?? []) : uncontrolled());
  const isOpen = (panel: ExpansionPanel): boolean => openIds().includes(panel.id);
  const isPanelDisabled = (panel: ExpansionPanel): boolean => !!props.disabled || !!panel.disabled;

  const headerId = (panel: ExpansionPanel): string => `weave-expansion-${uid}-${panel.id}-header`;
  const regionId = (panel: ExpansionPanel): string => `weave-expansion-${uid}-${panel.id}-region`;

  const toggle = (panel: ExpansionPanel): void => {
    if (isPanelDisabled(panel)) return;
    const open: boolean = isOpen(panel);
    let next: string[];
    if (multi()) {
      next = open ? openIds().filter((id) => id !== panel.id) : [...openIds(), panel.id];
    } else {
      next = open ? [] : [panel.id];
    }
    if (props.value === undefined) uncontrolled.set(next);
    props.onChange?.(next);
  };

  const onHeaderKeydown = (event: KeyboardEvent): void => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const el: Element | null = host();
    if (!el) return;
    const headers: HTMLElement[] = Array.from(
      el.querySelectorAll<HTMLElement>('.weave-expansion__header')
    ).filter((h) => h.getAttribute('aria-disabled') !== 'true');
    if (!headers.length) return;
    const current: number = headers.indexOf(document.activeElement as HTMLElement);
    let index: number;
    if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = headers.length - 1;
    else if (event.key === 'ArrowDown') index = current < 0 ? 0 : (current + 1) % headers.length;
    else index = current < 0 ? headers.length - 1 : (current - 1 + headers.length) % headers.length;
    headers[index].focus();
    event.preventDefault();
  };

  // Bodies are arbitrary content — append them into their regions once the regions
  // are in the DOM (deferred to onMount, like Form Field's slotted-control wiring).
  onMount(() => {
    const el: Element | null = host();
    if (!el) return;
    const bodies: NodeListOf<HTMLElement> = el.querySelectorAll<HTMLElement>('.weave-expansion__content');
    panels().forEach((panel, i) => {
      bodies[i]?.append(toNode(panel.body));
    });
  });

  return {
    host,
    panels,
    rootClass: (): string => (props.class ? `weave-expansion ${props.class}` : 'weave-expansion'),
    headingLevel: (): number => props.headingLevel ?? 3,
    headerId,
    regionId,
    expandedAttr: (panel): string => (isOpen(panel) ? 'true' : 'false'),
    markerIcon: (panel): string => (isOpen(panel) ? 'minus' : 'plus'),
    openAttr: (panel): string | undefined => (isOpen(panel) ? 'true' : undefined),
    hiddenAttr: (panel): string | undefined => (isOpen(panel) ? undefined : 'true'),
    disabledAttr: (panel): string | undefined => (isPanelDisabled(panel) ? 'true' : undefined),
    isClosed: (panel): boolean => !isOpen(panel),
    toggle,
    onHeaderKeydown,
  };
}

import { defineComponent } from "@weave-framework/runtime/dom";

import { bindAttr, bindProp, bindText, child, clone, eachBlock, listen, mountChild, setRef, template } from "@weave-framework/runtime/dom";

const _t0 = template("<div data-w-clgvh1 class=\"weave-expansion__panel\"><div data-w-clgvh1 class=\"weave-expansion__heading\" role=\"heading\"><button data-w-clgvh1 class=\"weave-expansion__header\" type=\"button\"><span data-w-clgvh1 class=\"weave-expansion__title\"><!----><\/span><span data-w-clgvh1 class=\"weave-expansion__marker\" aria-hidden=\"true\"><!----><\/span><\/button><\/div><div data-w-clgvh1 class=\"weave-expansion__region\" role=\"region\"><div data-w-clgvh1 class=\"weave-expansion__body\"><div data-w-clgvh1 class=\"weave-expansion__content\"><\/div><\/div><\/div><\/div>");
const _t1 = template("<div data-w-clgvh1><!----><\/div>");
function render(ctx, slots) {
  const _r = clone(_t1);
  const _n0 = child(_r, 0);
  bindAttr(_r, "class", () => (ctx.rootClass()));
  setRef(ctx.host, _r);
  eachBlock(_n0, () => ctx.panels(), (panel, $index) => panel.id, _b0)
  return _r;
  function _b0(_row0) {
  const _r = clone(_t0);
  const _n0 = child(_r, 0);
  const _n1 = child(_r, 0, 0);
  const _n2 = child(_r, 0, 0, 0, 0);
  const _n3 = child(_r, 0, 0, 1, 0);
  const _n4 = child(_r, 1);
  bindAttr(_n0, "aria-level", () => (ctx.headingLevel()));
  bindAttr(_n1, "id", () => (ctx.headerId(_row0.item())));
  bindAttr(_n1, "aria-expanded", () => (ctx.expandedAttr(_row0.item())));
  bindAttr(_n1, "aria-controls", () => (ctx.regionId(_row0.item())));
  bindAttr(_n1, "aria-disabled", () => (ctx.disabledAttr(_row0.item())));
  listen(_n1, "click", () => ctx.toggle(_row0.item()));
  listen(_n1, "keydown", ctx.onHeaderKeydown);
  bindText(_n2, () => (_row0.item().header));
  mountChild(_n3, Icon({ get name() { return ctx.markerIcon(_row0.item()); } }, {}));
  bindAttr(_n4, "id", () => (ctx.regionId(_row0.item())));
  bindAttr(_n4, "aria-labelledby", () => (ctx.headerId(_row0.item())));
  bindAttr(_n4, "data-open", () => (ctx.openAttr(_row0.item())));
  bindAttr(_n4, "aria-hidden", () => (ctx.hiddenAttr(_row0.item())));
  bindProp(_n4, "inert", () => (ctx.isClosed(_row0.item())));
  return _r;
}
}

export default defineComponent(render, setup);